<template>
  <component
    :is="rowComponent"
    v-model="isNewAttributeValid"
    ref="attribute-editing-table-row"
    no-gutters
    align="baseline"
    style="display: table-row"
    :class="{ 'border-top': index !== 0, 'new-attribute-row': isNewAttribute }"
  >
    <template v-if="isNewAttribute">
      <div v-for="header in headers" :key="header.value" class="customtable-tablecell">
        <v-tooltip v-if="header.value === 'desc_attribute_name'" top color="font">
          <template v-slot:activator="{ on }">
            <span v-on="on" class="d-flex justify-center">
              <v-radio disabled />
            </span>
          </template>
          <span>{{ $i18n.t('dialog.descriptionNameTooltip') }}</span>
        </v-tooltip>
        <div v-else-if="header.value === 'options'" class="d-flex justify-center">
          <v-icon
            :style="`cursor: ${isNewAttributeValid ? 'pointer' : 'default'}`"
            :color="isNewAttributeValid ? 'success' : 'grey'"
            @click="handleAttributeAdd(newAttribute)"
          >
            mdi-check
          </v-icon>
        </div>
        <data-input
          v-else
          v-model="newAttribute[header.value]"
          v-bind="headersInputProps[header.value]"
          v-on="headersInputProps[header.value].listeners"
        />
      </div>
    </template>
    <template v-else>
      <div v-for="header in headers" :key="header.value" class="columnElementText customtable-tablecell">
        <div v-if="header.value === 'verbose_name'" class="px-5">
          <data-input
            v-if="!attribute.predefined || !isSqlDataSource"
            class="pt-4"
            v-model="attribute.verbose_name"
            @change="setAttributeHandler($event, attribute, 'verbose_name')"
            :data-type="dataTypeText"
          >
          </data-input>
          <div v-else>
            {{ attribute.verbose_name }}
          </div>
        </div>
        <span v-if="header.value === 'name'">{{ attribute.name }}</span>
        <span v-if="header.value === 'data_type'">
          {{
            dataTypes.length > 0
              ? dataTypes.find(dT => dT.name === attribute.data_type.name).verbose_name
              : attribute.data_type.name
          }}</span
        >
        <div v-if="header.value === 'desc_attribute_name'" class="d-flex justify-center">
          <v-radio-group v-model="descAttributeProxy">
            <v-radio :value="attribute.name" />
          </v-radio-group>
        </div>
        <div v-if="header.value === 'read_only'" class="pt-4 d-flex justify-center">
          <data-input
            v-model="attribute.read_only"
            :data-type="{ name: 'checkbox' }"
            :disabled="attribute.predefined"
            @change="setAttributeHandler($event, attribute, 'read_only')"
          />
        </div>
        <div v-if="header.value === 'dict_id'" style="overflow: hidden">
          <v-tooltip v-if="!isSqlDataSource" bottom color="font" :disabled="!attribute.read_only">
            <template #activator="{ on }">
              <span v-on="on">
                <data-input
                  :disabled="attribute.read_only || !!attribute.relation"
                  v-model="attribute.dict_id"
                  :menu-props="{ offsetY: true, minWidth: '300px', maxWidth: '300px' }"
                  :items="getAttributeDicts(attribute)"
                  item-value="id"
                  item-text="name"
                  clearable
                  @change="setAttributeHandler($event, attribute, 'dict_id')"
                  class="px-5 pt-4"
                  :data-type="dataType"
                >
                </data-input>
              </span>
            </template>
            {{ $i18n.t('dialog.body.readyOnlyDict') }}
          </v-tooltip>
          <span v-else>
            {{ getAttributeDicts(attribute)?.find(dict => dict.id === attribute.dict_id)?.name || '' }}
          </span>
        </div>
        <div v-if="header.value === 'relation'">
          <v-tooltip v-if="!isSqlDataSource" bottom color="font" :disabled="!attribute.read_only">
            <template #activator="{ on }">
              <span v-on="on">
                <data-input
                  :disabled="attribute.read_only || !!attribute.dict_id"
                  :value="getRelationValue(attribute)"
                  :menu-props="{ offsetY: true, minWidth: '300px', maxWidth: '300px' }"
                  :items="computedRelations"
                  item-value="relationKey"
                  item-text="name"
                  return-object
                  clearable
                  @change="setAttributeHandler($event, attribute, 'relation')"
                  class="px-5 pt-4"
                  :data-type="dataType"
                >
                </data-input>
              </span>
            </template>
            {{ $i18n.t('dialog.body.readOnlyRelation') }}
          </v-tooltip>
          <span v-else>
            {{ computedRelations.find(relation => relation.relationKey === getRelationValue(attribute))?.name || '' }}
          </span>
        </div>
        <div v-if="header.value === 'measurement_unit_id'" class="pt-4">
          <data-input
            :menu-props="{ offsetY: true, minWidth: '300px', maxWidth: '300px' }"
            :items="formattedMeasurementUnits"
            item-value="id"
            item-text="text"
            class="px-5"
            clearable
            @change="setAttributeHandler($event, attribute, 'measurement_unit_id')"
            v-model="attribute.measurement_unit"
            :data-type="dataType"
            disabled-sorting
          >
          </data-input>
        </div>
        <v-tooltip v-if="header.value === 'options'" bottom color="font" :disabled="!attribute.predefined">
          <template #activator="{ on }">
            <span v-on="on" class="d-flex justify-center">
              <v-icon :disabled="attribute.predefined" color="error" @click="() => $emit('deleteAttribute', attribute)">
                mdi-close
              </v-icon>
            </span>
          </template>
          {{ $i18n.t('dialog.deleteAttributesInfo') }}
        </v-tooltip>
      </div>
    </template>
  </component>
</template>

<script>
import DataInput from '@/components/DataInput';
import { VForm } from 'vuetify/lib';

import { get, call } from 'vuex-pathify';

export default {
  name: 'DataSourceAttributesEditingTableRow',
  components: {
    DataInput,
  },
  props: {
    isNewAttribute: {
      type: Boolean,
      default: false,
    },
    headers: {
      type: Array,
      default: () => [],
    },
    attribute: {
      type: Object,
      default: () => ({}),
    },
    index: {
      type: Number,
      default: null,
    },
    dataSource: {
      type: Object,
      default: () => ({}),
    },
    descAttribute: {
      type: String,
      default: '',
    },
    isVisible: {
      type: Boolean,
      default: true,
    },
  },
  data: () => ({
    isNewAttributeValid: false,
    newAttribute: {
      dict_id: undefined,
    },
    dataTypeText: {
      name: 'text',
    },
    dataType: {
      name: 'select',
    },
  }),
  computed: {
    dictionaries: get('admin/dictionaries'),
    dataTypes: get('layers/dataTypes'),
    datasources: get('layers/metadata'),
    computedDataSource: {
      get() {
        return this.dataSource;
      },
      set(nV) {
        this.$emit('update:dataSource', nV);
      },
    },
    descAttributeProxy: {
      get() {
        return this.descAttribute;
      },
      set(nV) {
        this.$emit('update:descAttribute', nV);
      },
    },
    isSqlDataSource() {
      return this.computedDataSource?.datasource_type === 'sql_simple_local';
    },
    headersInputProps() {
      return {
        verbose_name: {
          dataType: this.dataTypeText,
          rules: [v => !!v || this.$i18n.t('rules.required')],
          class: 'px-5 ma-0',
        },
        name: {
          dataType: this.dataTypeText,
          rules: [v => /^[a-zA-Z]+[\w\d]*$/.test(v || '') || this.$i18n.t('rules.incorrectValue')],
          class: 'px-5 py-0 ma-0',
        },
        data_type: {
          menuProps: {
            offsetY: true,
            minWidth: '200px',
          },
          items: this.excludedDataTypes,
          itemText: 'verbose_name',
          itemValue: 'name',
          listeners: {
            change: () => {
              this.newAttribute.dict_id = undefined;
            },
          },
          class: 'px-5',
          dataType: this.dataType,
        },
        read_only: {
          dataType: { name: 'checkbox' },
          class: 'd-flex justify-center',
        },
        dict_id: {
          ...(!this.newAttribute.data_type && { tooltip: this.$i18n.t('dialog.body.dataTypeDict') }),
          menuProps: { offsetY: true, minWidth: '300px', maxWidth: '300px' },
          items: Object.values(this.dictionaries).filter(d => d.data_type === this.newAttribute.data_type),
          itemValue: 'id',
          itemText: 'name',
          clearable: true,
          disabled: !this.newAttribute.data_type || !!this.newAttribute.relation,
          class: 'px-5',
          dataType: this.dataType,
        },
        relation: {
          ...(!this.newAttribute.data_type && { tooltip: this.$i18n.t('dialog.body.dataTypeRelation') }),
          menuProps: { offsetY: true, minWidth: '300px', maxWidth: '300px' },
          items: this.computedRelations,
          itemValue: 'relationKey',
          itemText: 'name',
          returnObject: true,
          clearable: true,
          disabled: !this.newAttribute.data_type || !!this.newAttribute.dict_id,
          class: 'px-5',
          dataType: this.dataType,
        },
        measurement_unit_id: {
          menuProps: { offsetY: true, minWidth: '300px', maxWidth: '300px' },
          items: this.formattedMeasurementUnits,
          itemText: 'text',
          itemValue: 'id',
          clearable: true,
          class: 'px-5',
          dataType: this.dataType,
          disabledSorting: true,
        },
      };
    },
    rowComponent() {
      return this.isNewAttribute ? VForm : 'div';
    },
    excludedDataTypes() {
      return this.dataTypes.filter(d => d.name !== 'list' && d.name !== 'geometric');
    },
    relations: get('admin/allowedRelations'),
    computedRelations() {
      return this.relations.map(relation => {
        const { attribute, data_source, representation } = relation;
        const relationKey = `${data_source}-${attribute}-${representation}`;
        return { ...relation, relationKey };
      });
    },
    measurementUnits: get('layers/measurementUnits'),
    formattedMeasurementUnits() {
      let formattedMeasurementUnits = [];
      for (let measurementUnitGroup of Object.keys(this.measurementUnits)) {
        formattedMeasurementUnits.push({ header: measurementUnitGroup });
        formattedMeasurementUnits = [
          ...formattedMeasurementUnits,
          ...this.measurementUnits[measurementUnitGroup].map(mU => {
            return { id: mU.id, text: `${mU.name} [${mU.symbol}]` };
          }),
        ];
        formattedMeasurementUnits.push({ divider: true });
      }
      return formattedMeasurementUnits;
    },
  },
  watch: {
    isVisible(nV) {
      if (!nV) return;
      this.newAttribute = { dict_id: undefined };
      this.$refs['attribute-editing-table-row']?.resetValidation();
    },
  },
  methods: {
    setAttribute: call('layers/setAttribute'),
    addAttribute: call('layers/addAttribute'),
    async handleAttributeAdd(newAttribute = this.newAttribute) {
      newAttribute = JSON.parse(JSON.stringify(newAttribute));
      if (!this.isNewAttributeValid) {
        return;
      } else if (!newAttribute.name) {
        this.$store.set('snackbar/PUSH_ERROR!', {
          message: this.$i18n.t('errors.attributeNameRequired'),
        });
      } else if (newAttribute.name.includes(' ')) {
        this.$store.set('snackbar/PUSH_ERROR!', {
          message: this.$i18n.t('errors.attributeNameContainSpaces'),
        });
      } else if (!newAttribute.data_type) {
        this.$store.set('snackbar/PUSH_ERROR!', {
          message: this.$i18n.t('errors.attributeTypeRequired'),
        });
      } else {
        if (!newAttribute.dict_id && newAttribute.dict_id !== 0) {
          newAttribute.dict_id = null;
        }
        if (!newAttribute.relation) {
          newAttribute.relation = null;
        } else {
          const { attribute, data_source, representation } = newAttribute.relation;
          newAttribute.relation = { attribute, data_source, representation };
        }
        const payload = {
          data: newAttribute,
          dataSourceName: this.computedDataSource.name,
        };
        const r = await this.addAttribute(payload);
        if (!this.isDataSourceFromLayer) {
          this.computedDataSource.attributes_schema.attributes.push(r.data.data);
        }
        this.$root.$emit('attributesChanged');
        this.newAttribute = {};
        this.$refs?.['attribute-editing-table-row']?.resetValidation();
      }
    },
    getRelationValue(attribute) {
      return attribute.relation
        ? `${attribute.relation.data_source}-${attribute.relation.attribute}-${attribute.relation.representation}`
        : '';
    },
    getAttributeDicts(attribute) {
      return Object.values(this.dictionaries).filter(d => d.data_type === attribute.data_type.name);
    },
    async setAttributeHandler(value, attribute, type) {
      if (!value && value !== false) {
        value = null;
      } else if (type === 'relation') {
        const { data_source, attribute, representation } = value;
        value = { data_source, attribute, representation };
      }
      const payload = {
        attributeName: attribute.name,
        dataSourceName: this.computedDataSource.name,
        type,
        value,
      };
      try {
        const r = await this.setAttribute(payload);
        this.computedDataSource.attributes_schema.attributes.map(a => {
          return a.name === attribute.name ? r.data.data : a;
        });
        this.$root.$emit('attributesChanged');
      } catch {
        this.handleSetAttributeError(attribute.name, type);
      }
    },
    handleSetAttributeError(attributeName, type) {
      const errorAttribute = this.computedDataSource.attributes_schema.attributes.find(aT => aT.name === attributeName);
      const errorAttributeIdx = this.computedDataSource.attributes_schema.attributes.indexOf(errorAttribute);
      const key = type === 'dict_id' ? 'dict_id' : 'measurement_unit';
      const properValue = this.datasources[this.computedDataSource.name].attributes_schema.attributes.find(
        aT => aT.name === attributeName
      )[key];
      this.computedDataSource.attributes_schema.attributes[errorAttributeIdx][key] = properValue;
    },
  },
};
</script>

<style lang="scss" scoped>
.columnElementText {
  font-family: Roboto;
  font-style: normal;
  font-weight: normal;
  font-size: 13px;
  line-height: 16px;
  overflow: hidden;
  text-align: center;
}

.new-attribute-row ::v-deep .customtable-tablecell {
  vertical-align: baseline !important;
  padding-top: 16px;
}

::v-deep {
  .customtable-tablecell {
    max-width: 150px;
  }
  .v-input--selection-controls__input {
    margin: 0 !important;
  }
}
</style>
